/**
 * core · usuários internos (spec 03 §2 · spec 02 §1.7 · DS-37 · bloco Usuários da 10.1).
 *
 * Usuário interno NÃO é item de catálogo: tem estados, convite, sessão e segundo
 * fator. É por isso que ele não passa pela rota genérica `/catalogos/:tipo` —
 * ativar/desativar uma linha de catálogo e suspender uma pessoa são atos diferentes
 * com consequências diferentes.
 */
import { z } from 'zod'
import { dataNominal, email, uuid } from './comum.js'

/**
 * Um usuário interno CITADO por outro registro: o responsável da tarefa, o autor do comentário
 * e as menções, o autor da notificação, a pessoa do seletor de responsável (PK-04 · DS-518).
 *
 * `avatarAnexoId` é OBRIGATÓRIO e nulável, nunca opcional: opcional, um produtor ou uma
 * fixture que esquecesse o campo ficaria verde, e a foto sumiria sem ninguém notar. A tela
 * monta o endereço da foto com `urlDoAvatar` — o payload leva só o id.
 */
export const referenciaDeUsuario = z.object({
  id: uuid,
  nome: z.string(),
  avatarAnexoId: uuid.nullable(),
})
export type ReferenciaDeUsuario = z.infer<typeof referenciaDeUsuario>

/** A régua de estados da DS-37, na ordem em que a vida do acesso acontece. */
export const estadoDeUsuario = z.enum(['convidado', 'ativo', 'suspenso', 'desligado'])
export type EstadoDeUsuario = z.infer<typeof estadoDeUsuario>

export const usuario = z.object({
  id: uuid,
  nome: z.string(),
  email: z.string(),
  funcaoLivre: z.string().nullable(),
  nivelAcessoId: uuid,
  nivelAcessoNome: z.string(),
  estado: estadoDeUsuario,
  expiraEm: dataNominal.nullable(),
  ultimoAcessoEm: z.string().nullable(),
  doisFatoresObrigatorio: z.boolean(),
  /** Já constituiu o fator? É o que separa "tem 2FA" de "deve ter e ainda não tem". */
  temSegundoFator: z.boolean(),
})
export type Usuario = z.infer<typeof usuario>

/**
 * Convidar alguém.
 *
 * Sem default para `nivelAcessoId` de propósito: a coluna é NOT NULL (spec 02 §1.7)
 * e escolher um nível por conta própria seria decidir o que a pessoa acessa. A tela
 * obriga a escolha.
 */
export const entradaDeNovoUsuario = z.object({
  nome: z.string().min(1, 'Informe o nome completo.'),
  email,
  nivelAcessoId: uuid,
  expiraEm: dataNominal.nullish(),
  /**
   * Vem travado quando o nível exige (spec 05 §9) — o servidor não confia no
   * cliente: recalcula a partir do nível e nunca aceita `false` onde a derivação
   * diz `true`.
   */
  doisFatoresObrigatorio: z.boolean().optional(),
})
export type EntradaDeNovoUsuario = z.infer<typeof entradaDeNovoUsuario>

export const entradaDeEdicaoDeUsuario = z.object({
  nome: z.string().min(1, 'Informe o nome completo.').optional(),
  nivelAcessoId: uuid.optional(),
  expiraEm: dataNominal.nullish(),
  doisFatoresObrigatorio: z.boolean().optional(),
})
export type EntradaDeEdicaoDeUsuario = z.infer<typeof entradaDeEdicaoDeUsuario>

/**
 * O convite devolve o link.
 *
 * No v1 o e-mail sai pelo adapter da fase 5.2; até lá o link volta na resposta para
 * quem convidou repassar. Mostrar o link é melhor que fingir que um e-mail saiu:
 * uma tela que diz "convite enviado" sem servidor de e-mail é uma mentira que só
 * aparece quando a pessoa não recebe.
 */
export const respostaDeConvite = z.object({
  usuario,
  linkDeConvite: z.string(),
  expiraEm: z.string(),
})
export type RespostaDeConvite = z.infer<typeof respostaDeConvite>
